import { api } from '@/shared/api';
import { apiRoutes } from '@/shared/routes';
import { Account, AcceptedDateRange, CreateAccountResponse, DateRangeSpan, Schedule, ScheduleActivity } from './types';
import type { WireRequest, WireResponse } from '@/shared/generated/wire';
import { toSchedule } from './wire';

export function fetchAccounts(): Promise<Account[]> {
  return api.get<WireResponse<'/app/api/accounts', 'get', 200>>(apiRoutes.accounts);
}

export function fetchSchedules(bookable: boolean): Promise<Schedule[]> {
  return api.get<WireResponse<'/app/api/schedules', 'get', 200>>(`${apiRoutes.schedules}?bookable=${bookable}`)
    .then((schedules) => schedules.map((schedule) => toSchedule(schedule)));
}

export function createAccount(payload: {
  email: string;
  password: string;
  to_country_code: string;
}): Promise<CreateAccountResponse> {
  return api.post<WireResponse<'/app/api/accounts', 'post', 201>>(apiRoutes.accounts,
    payload satisfies WireRequest<'/app/api/accounts', 'post'>)
    .then(({ account, schedules }) => ({ account, schedules: schedules.map((schedule) => toSchedule(schedule)) }));
}

export function updateAccountPassword(accountId: number, password: string): Promise<Account> {
  return api.patch<WireResponse<'/app/api/accounts/{id}', 'patch', 200>>(apiRoutes.account(accountId),
    { password } satisfies WireRequest<'/app/api/accounts/{id}', 'patch'>);
}

export function deleteAccount(accountId: number): Promise<null> {
  return api.del<WireResponse<'/app/api/accounts/{id}', 'delete', 204>>(apiRoutes.account(accountId));
}

export async function pauseSchedule(scheduleId: number): Promise<void> {
  await api.post<WireResponse<'/app/api/schedules/{id}/pause', 'post', 200>>(apiRoutes.schedulePause(scheduleId), {});
}

export async function activateSchedule(scheduleId: number): Promise<void> {
  await api.post<WireResponse<'/app/api/schedules/{id}/activate', 'post', 200>>(apiRoutes.scheduleActivate(scheduleId), {});
}

export interface SchedulePreferencesPayload {
  accept: AcceptedDateRange[];
  exclude: DateRangeSpan[];
  max_gap_between_asc_and_consular: number | null;
  min_days_from_now: number | null;
  min_same_day_gap: number | null;
  allow_cancellation: boolean;
  accept_worse_date: boolean;
  preferred_facilities: string[];
}

export function reactivateSchedule(
  scheduleId: number,
  preferences: SchedulePreferencesPayload
): Promise<Schedule> {
  return api.post<WireResponse<'/app/api/schedules/{schedule_id}/reactivation', 'post', 201>>(
    apiRoutes.scheduleReactivation(scheduleId),
    preferences satisfies WireRequest<'/app/api/schedules/{schedule_id}/reactivation', 'post'>
  ).then(toSchedule);
}

export function fetchScheduleActivities(scheduleId: number): Promise<ScheduleActivity[]> {
  return api.get<WireResponse<'/app/api/schedules/{id}/activities', 'get', 200>>(apiRoutes.scheduleActivities(scheduleId));
}

export function updateSchedulePreferences(
  scheduleId: number,
  preferences: SchedulePreferencesPayload
): Promise<null> {
  return api.put<WireResponse<'/app/api/schedules/{id}/preferences', 'put', 204>>(
    apiRoutes.schedulePreferences(scheduleId),
    preferences satisfies WireRequest<'/app/api/schedules/{id}/preferences', 'put'>
  );
}
